import { NgTemplateOutlet } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  type OnInit,
  output,
  ViewChild,
} from "@angular/core";
import { RouterModule } from "@angular/router";

import { HotkeysService } from "@ngneat/hotkeys";
import { TranslateModule } from "@ngx-translate/core";
import { DialogModule } from "primeng/dialog";
import { type Popover, PopoverModule } from "primeng/popover";

import { AuthService } from "@/app/core/services/auth.service";
import { ShortcutComponent } from "@/app/shared/components/shortcut/shortcut.component";
import { APP_PATHS } from "@/app/shared/constants";

/**
 * User menu component that displays user information and provides navigation options.
 * Includes logout functionality and help dialog with keyboard shortcuts.
 */
@Component({
  selector: "app-user-menu",
  imports: [
    PopoverModule,
    RouterModule,
    DialogModule,
    ShortcutComponent,
    TranslateModule,
    NgTemplateOutlet,
  ],
  templateUrl: "user-menu.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserMenuComponent implements OnInit {
  readonly hotkeysService = inject(HotkeysService);
  private readonly authService = inject(AuthService);

  closeDrawerOnMobile = output<void>();

  @ViewChild("popover") popover!: Popover;

  displayHelp = false;
  userName = "";
  userMail = "";

  readonly profileUrl = APP_PATHS.userProfile;

  /**
   * Retrieves and formats user information.
   */
  ngOnInit(): void {
    const userInfo = this.authService.getIAMUserInfo();
    this.userName = `${userInfo.firstname} ${userInfo.lastname.charAt(0)}.`;
    this.userMail = userInfo.email;
  }

  /**
   * Closes the user menu by hiding the popover and emitting close event for mobile.
   */
  closeMenu(): void {
    this.closeDrawerOnMobile.emit();
    this.popover.hide();
  }

  /**
   * Logs out the current user using the authentication service.
   */
  logout(): void {
    this.authService.logout();
  }
}
